/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Radio,
  AutoComplete,
} from 'antd';
import { OnFetchShipmentSno } from '../../redux/ShipmentDetails/saga';
import { OnAddorUpdateShipmentsDashboard } from '../../redux/ShipmentDashboardDetails/saga';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
  packagingFormValueToApi,
  getPopupContainerBody,
} from './shipmentFieldOptions';

const { Option } = Select;

const AddNewShipments = (props) => {
  const {
    showAddModel,
    setShowAddModel,
    editRecord,
    isEdit,
    setIsEdit,
    setEditRecord,
    OnfetchData,
    selectedProject,
    adminShipmentTypesList,
  } = props;

  const [shipmentType, setShipmentType] = useState('');
  const [ShipmentNumber, setShipmentNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [equipTypeForm] = Form.useForm();

  const [serialNoList, setSerialNoList] = useState([]);
  const [serialNo, setSerialNo] = useState('');
  const [shipmentTyCategory, setShipmentTypeCategory] = useState('');
  const [serialNoLoading, setSerialNoLoading] = useState(false);

  const [priority, setPriority] = useState('medium');
  const [shipmentCategory, setShipmentCategory] = useState('project');
  const [packagingSubcategory, setPackagingSubcategory] = useState(undefined);

  const OnfetchSerialNumberData = useCallback(async (selectedShipmentType) => {
    setSerialNoLoading(true);
    const obj = { id: selectedShipmentType.id };
    setShipmentType(selectedShipmentType.name);
    const res = await OnFetchShipmentSno(obj, 'false');
    const list = res?.shipmentType?.shipments && res.shipmentType.shipments.length > 0
      ? res.shipmentType.shipments
      : [];
    setSerialNoList(list);
    setSerialNoLoading(false);
    return list;
  }, []);

  useEffect(() => {
    if (!isEdit || !editRecord || !adminShipmentTypesList?.length) return;

    const run = async () => {
      const meta = normalizeShipmentMetaFromRecord(editRecord);
      setPriority(meta.priority);
      setShipmentCategory(meta.shipmentCategory);
      setPackagingSubcategory(meta.packagingSubcategory);

      const shipmentArr = adminShipmentTypesList.filter(
        (item) => item.name === editRecord.shipmentTypeName
      );
      if (!shipmentArr.length) return;

      setShipmentTypeCategory(shipmentArr[0]);
      setShipmentType(shipmentArr[0].name);
      const list = await OnfetchSerialNumberData(shipmentArr[0]);
      const num = editRecord.shipmentNumber;
      setShipmentNumber(num || '');
      const match = list.find((item) => item.shipmentNumber === num);
      setSerialNo(match || (num ? { shipmentNumber: num } : ''));
    };

    run();
  }, [isEdit, editRecord, adminShipmentTypesList, OnfetchSerialNumberData]);

  /** When opening Add (not edit), ensure meta fields are visible defaults */
  useEffect(() => {
    if (showAddModel && !isEdit) {
      const meta = normalizeShipmentMetaFromRecord(null);
      setPriority(meta.priority);
      setShipmentCategory(meta.shipmentCategory);
      setPackagingSubcategory(undefined);
    }
  }, [showAddModel, isEdit]);

  const onChangeShipmentType = async (value) => {
    setSerialNo('');
    setShipmentNumber('');
    if (value) {
      const shipmentArr = adminShipmentTypesList?.filter((item) => item.name === value) || [];
      if (shipmentArr.length > 0) {
        setShipmentTypeCategory(shipmentArr[0]);
        await OnfetchSerialNumberData(shipmentArr[0]);
      } else {
        setShipmentTypeCategory('');
        setShipmentType('');
        setSerialNoList([]);
      }
    } else {
      setShipmentTypeCategory('');
      setShipmentType('');
      setSerialNoList([]);
      setShipmentNumber('');
    }
  };

  const onShipmentNumberChange = (value) => {
    const v = typeof value === 'string' ? value.trim() : '';
    setShipmentNumber(v);
    if (!v) {
      setSerialNo('');
      return;
    }
    const match = serialNoList.find((item) => item.shipmentNumber === v);
    setSerialNo(match || { shipmentNumber: v });
  };

  const autoCompleteOptions = serialNoList.map((item) => ({
    value: item.shipmentNumber,
    label: item.shipmentNumber,
  }));

  const handleOk = async () => {
    if (shipmentTyCategory === '') {
      notification.warning({
        message: 'Please select shipment type to proceed!',
      });
      return;
    }
    const num = (ShipmentNumber || '').trim();
    if (!num) {
      notification.warning({
        message: 'Please enter or select a shipment number to proceed!',
      });
      return;
    }
    if (!packagingSubcategory) {
      notification.warning({
        message: 'Please select packaging (subcategory) to proceed!',
      });
      return;
    }

    const packagingType = packagingFormValueToApi(packagingSubcategory);
    const data = {
      shipmentTypeId: shipmentTyCategory.id,
      projectId: selectedProject.id,
      status: 1,
      priority,
      shipmentCategory,
      packagingType,
    };

    const matched = serialNoList.find((item) => item.shipmentNumber === num);
    if (matched?.id) {
      data.shipmentId = matched.id;
    } else if (serialNo?.id) {
      data.shipmentId = serialNo.id;
    } else {
      data.shipmentNumber = num;
    }

    let payload = data;
    if (isEdit) {
      payload = {
        id: editRecord.pihShipmentId,
        data,
      };
    }

    setAddLoading(true);
    const res = await OnAddorUpdateShipmentsDashboard(payload, isEdit);

    if (res) await OnfetchData();
    else {
      notification.warning({
        message: `${shipmentType} and ${ShipmentNumber} could not be saved. It may already exist.`,
      });
    }

    setAddLoading(false);
    resetForm();
    setShowAddModel(false);
  };

  const resetForm = () => {
    setShipmentType('');
    setShipmentNumber('');
    setSerialNo('');
    setSerialNoList([]);
    setShipmentTypeCategory('');
    setPriority('medium');
    setShipmentCategory('project');
    setPackagingSubcategory(undefined);
    setIsEdit(false);
    if (setEditRecord) setEditRecord('');
  };

  const handleCancel = () => {
    setShowAddModel(false);
    resetForm();
  };

  return (
    <div>
      <Modal
        className="pipeline-modal shipment-add-modal"
        title={isEdit ? 'Update Shipment Data' : 'Add New Shipment Data'}
        centered
        visible={showAddModel}
        width={640}
        destroyOnClose
        bodyStyle={{
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
          padding: '16px 24px 8px',
        }}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
          >
            {'Cancel'}
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleOk}
            loading={addLoading}
          >
            {isEdit ? 'Update' : 'Add'}
          </Button>,
        ]}
      >
        <Form
          form={equipTypeForm}
          name="basic"
          layout="vertical"
          autoComplete="off"
          getContainer={false}
        >
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item
                label="Priority"
                required
              >
                <Select
                  className="client-select"
                  placeholder="Select priority"
                  value={priority}
                  onChange={setPriority}
                  getPopupContainer={getPopupContainerBody}
                >
                  {SHIPMENT_PRIORITY_OPTIONS.map((o) => (
                    <Option
                      key={o.value}
                      value={o.value}
                    >
                      {o.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item
                label="Category"
                required
              >
                <Radio.Group
                  className="shipment-category-radio-group"
                  value={shipmentCategory}
                  onChange={(e) => setShipmentCategory(e.target.value)}
                >
                  {SHIPMENT_CATEGORY_OPTIONS.map((o) => (
                    <Radio
                      key={o.value}
                      value={o.value}
                    >
                      {o.label}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item
                label="Subcategory (packaging)"
                required
              >
                <Select
                  className="client-select"
                  placeholder="Select packaging type"
                  allowClear
                  value={packagingSubcategory}
                  onChange={setPackagingSubcategory}
                  getPopupContainer={getPopupContainerBody}
                >
                  {SHIPMENT_PACKAGING_OPTIONS.map((o) => (
                    <Option
                      key={o.value}
                      value={o.value}
                    >
                      {o.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col
              xs={24}
              sm={24}
              md={12}
            >
              <Form.Item
                label="Shipment Type"
                name="Shipment Type"
                required
              >
                <Select
                  className="client-select"
                  placeholder="Select  Shipment type"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  onChange={onChangeShipmentType}
                  value={shipmentType === '' ? null : shipmentType}
                  getPopupContainer={getPopupContainerBody}
                >
                  {adminShipmentTypesList &&
                    adminShipmentTypesList.map((item, i) => (
                      <Option
                        key={i}
                        value={item && item.name}
                      >
                        {item && item.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={12}
            >
              <Form.Item
                label="Shipment Number"
                name="shipmentNumber"
                required
                extra="Choose from suggestions or type a shipment number."
              >
                <AutoComplete
                  className="client-select"
                  style={{ width: '100%' }}
                  options={autoCompleteOptions}
                  placeholder={
                    serialNoLoading ? 'Loading…' : 'Select or type shipment number'
                  }
                  disabled={shipmentType === ''}
                  value={ShipmentNumber}
                  onChange={onShipmentNumberChange}
                  getPopupContainer={getPopupContainerBody}
                  filterOption={(inputValue, option) =>
                    (option?.value || '')
                      .toString()
                      .toLowerCase()
                      .includes((inputValue || '').toLowerCase())
                  }
                >
                  <Input size="large" />
                </AutoComplete>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AddNewShipments;
